import { ImageFullscreen } from "@/components/modal/imageFullscreen";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { AccordionClass } from ".";
import { RelatedFieldsWithAttachments } from "../types";

interface RelatedWorksAccordionProps {
  relatedFieldsWithAttachments: RelatedFieldsWithAttachments;
  relationNotEmpty: boolean;
  t: (key: string) => string;
}

export function RelatedWorksAccordion({
  relatedFieldsWithAttachments,
  relationNotEmpty,
  t,
}: RelatedWorksAccordionProps) {
  if (!relationNotEmpty) return null;

  const getImageAlt = (item: any, type: string): string => {
    switch (type) {
      case "artwork":
        return item.oeuvresDescriptionTitre || "Artwork image";
      case "document":
        return item.documentsDescriptionTitre || "Document image";
      case "multipleprint":
        return item.estampesMultiplesDescriptionTitre || "Multipleprint image";
      case "photography":
        return item.photosDescriptionLegendeRedigee || "Photography image";
      case "writing":
        return item.documentsDescriptionTitre || "Writing image";
      case "audiovisual":
        return item.audiovisuelDescriptionResume || "Audiovisual image";
      default:
        return "Image";
    }
  };

  const getContent = (item: any, type: string): string => {
    switch (type) {
      case "artwork":
        return item.oeuvresDescriptionResume;
      case "document":
        return item.documentsDescriptionLegendeRedigee;
      case "multipleprint":
        return item.estampesMultiplesDescriptionResume;
      case "photography":
        return item.photosDescriptionLegendeRedigee;
      case "writing":
        return item.ecritsDescriptionLegendeRedigee;
      case "audiovisual":
        return item.audiovisuelDescriptionResume;
      default:
        return "";
    }
  };

  return (
    <AccordionItem value="oeuvres">
      <AccordionTrigger>
        {t("singleArtworkSubtitleAssociatedWorks")}
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 w-full">
        {relatedFieldsWithAttachments?.relatedArtwork?.map((artwork: any) => (
          <div key={artwork.id}>
            <div className="flex items-center gap-4 w-full">
              {artwork.attachments && artwork.attachments.length > 0 ? (
                <ImageFullscreen
                  media={{
                    attachments: artwork.attachments,
                    type: "artwork",
                    alt: getImageAlt(artwork, "artwork"),
                    width: 200,
                    height: 200,
                    className:
                      "h-[100px] min-w-[200px] aspect-auto object-right-top",
                  }}
                />
              ) : (
                <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                  <span className="text-gray-400">{t("noImage")}</span>
                </div>
              )}
              <div
                dangerouslySetInnerHTML={{
                  __html: getContent(artwork, "artwork"),
                }}
                className={AccordionClass}
              />
            </div>
          </div>
        ))}
        {relatedFieldsWithAttachments?.from_Artwork_relatedArtwork?.map(
          (artwork: any) => (
            <div key={artwork.id}>
              <div className="flex items-center gap-4 w-full">
                {artwork.attachments && artwork.attachments.length > 0 ? (
                  <ImageFullscreen
                    media={{
                      attachments: artwork.attachments,
                      type: "artwork",
                      alt: getImageAlt(artwork, "artwork"),
                      width: 200,
                      height: 200,
                      className:
                        "h-[100px] min-w-[200px] aspect-auto object-right-top",
                    }}
                  />
                ) : (
                  <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                    <span className="text-gray-400">{t("noImage")}</span>
                  </div>
                )}
                <div
                  dangerouslySetInnerHTML={{
                    __html: getContent(artwork, "artwork"),
                  }}
                  className={AccordionClass}
                />
              </div>
            </div>
          ),
        )}
        {relatedFieldsWithAttachments?.relatedDocument?.map((document: any) => (
          <div key={document.id}>
            <div className="flex items-center gap-4">
              <div className="max-w-[200px]">
                {document.attachments && document.attachments.length > 0 ? (
                  <ImageFullscreen
                    media={{
                      attachments: document.attachments,
                      type: "document",
                      alt: getImageAlt(document, "document"),
                      width: 200,
                      height: 200,
                      className:
                        "h-[100px] min-w-[200px] aspect-auto !object-right-top",
                    }}
                  />
                ) : (
                  <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                    <span className="text-gray-400">{t("noImage")}</span>
                  </div>
                )}
              </div>
              <div className={cn(AccordionClass, "flex-1")}>
                <p>{document.documentsDescriptionTitre}</p>
              </div>
            </div>
          </div>
        ))}
        {relatedFieldsWithAttachments?.relatedMultipleprint?.map(
          (multipleprint: any) => (
            <div key={multipleprint.id}>
              <div className="flex items-center gap-4">
                {multipleprint.attachments &&
                multipleprint.attachments.length > 0 ? (
                  <ImageFullscreen
                    media={{
                      attachments: multipleprint.attachments,
                      type: "multipleprint",
                      alt: getImageAlt(multipleprint, "multipleprint"),
                      width: 200,
                      height: 200,
                      className:
                        "h-[100px] min-w-[200px] aspect-auto !object-right-top",
                    }}
                  />
                ) : (
                  <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                    <span className="text-gray-400">{t("noImage")}</span>
                  </div>
                )}
                <div
                  dangerouslySetInnerHTML={{
                    __html: getContent(multipleprint, "multipleprint"),
                  }}
                  className={AccordionClass}
                />
              </div>
            </div>
          ),
        )}
        {relatedFieldsWithAttachments?.relatedPhotography?.map(
          (photography: any) => (
            <div key={photography.id}>
              <div className="flex gap-4 items-center">
                <div>
                  {photography.attachments &&
                  photography.attachments.length > 0 ? (
                    <ImageFullscreen
                      media={{
                        attachments: photography.attachments,
                        type: "photography",
                        alt: getImageAlt(photography, "photography"),
                        width: 200,
                        height: 200,
                        className:
                          "w-[200px] h-[100px] aspect-auto !object-right-top",
                      }}
                    />
                  ) : (
                    <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                      <span className="text-gray-400">{t("noImage")}</span>
                    </div>
                  )}
                </div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: getContent(photography, "photography"),
                  }}
                  className={cn(AccordionClass, "flex-1")}
                />
              </div>
            </div>
          ),
        )}
        {relatedFieldsWithAttachments?.relatedWriting?.map((writing: any) => (
          <div key={writing.id}>
            <div className="flex items-center gap-4">
              <div className="w-[200px]">
                {writing.attachments && writing.attachments.length > 0 ? (
                  <ImageFullscreen
                    media={{
                      attachments: writing.attachments,
                      type: "writing",
                      alt: getImageAlt(writing, "writing"),
                      width: 200,
                      height: 200,
                      className:
                        "h-[100px] min-w-[200px] aspect-auto !object-right-top",
                    }}
                  />
                ) : (
                  <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                    <span className="text-gray-400">{t("noImage")}</span>
                  </div>
                )}
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: getContent(writing, "writing"),
                }}
                className={cn(AccordionClass, "flex-1")}
              />
            </div>
          </div>
        ))}
        {relatedFieldsWithAttachments?.relatedAudiovisual?.map(
          (audiovisual: any) => (
            <div key={audiovisual.id}>
              <div className="flex items-center gap-4">
                <div className="w-[200px]">
                  {audiovisual.attachments &&
                  audiovisual.attachments.length > 0 ? (
                    <ImageFullscreen
                      media={{
                        attachments: audiovisual.attachments,
                        type: "audiovisual",
                        alt: getImageAlt(audiovisual, "audiovisual"),
                        width: 200,
                        height: 200,
                        className:
                          "h-[100px] min-w-[200px] aspect-auto object-right-top",
                      }}
                    />
                  ) : (
                    <div className="w-[100px] h-[100px] bg-gray-200 flex items-center justify-center rounded">
                      <span className="text-gray-400">{t("noImage")}</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getContent(audiovisual, "audiovisual"),
                    }}
                    className={AccordionClass}
                  />
                </div>
              </div>
            </div>
          ),
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
